import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X, HelpCircle } from "lucide-react";
import type { CrmUserFilters } from "@/services/adminCrm";

const STATUS_LEGEND: { state: string; className: string; label: string; description: string }[] = [
  {
    state: "b2b",
    className: "bg-green-100 text-green-800 border-green-200",
    label: "B2B",
    description: "Academy/school user — access covered by their organization",
  },
  {
    state: "active",
    className: "bg-green-100 text-green-800 border-green-200",
    label: "Active",
    description: "Paying subscriber with current Stripe subscription",
  },
  {
    state: "comp",
    className: "bg-blue-100 text-blue-800 border-blue-200",
    label: "Comp",
    description: "Admin-granted free access with expiration date",
  },
  {
    state: "canceling",
    className: "bg-amber-100 text-amber-800 border-amber-200",
    label: "Canceling",
    description: "Will cancel at period end — still has access until then",
  },
  {
    state: "past_due",
    className: "bg-red-100 text-red-800 border-red-200",
    label: "Past Due",
    description: "Payment failed — access blocked, Stripe retrying",
  },
  {
    state: "expired",
    className: "bg-red-100 text-red-800 border-red-200",
    label: "Expired",
    description: "Subscription period ended after cancellation",
  },
  {
    state: "canceled",
    className: "bg-red-100 text-red-800 border-red-200",
    label: "Canceled",
    description: "Subscription fully canceled — access blocked",
  },
  {
    state: "none",
    className: "bg-gray-100 text-gray-600 border-gray-200",
    label: "None",
    description: "Account created but never subscribed",
  },
];

interface UserListFiltersProps {
  filters: CrmUserFilters;
  onFilterChange: (filters: CrmUserFilters) => void;
}

const ACTIVATION_OPTIONS = [
  { value: "_all", label: "All Stages" },
  { value: "activated", label: "Activated" },
  { value: "invited", label: "Invited" },
  { value: "lapsed", label: "Lapsed" },
];

const SUBSCRIPTION_OPTIONS = [
  { value: "_all", label: "All Types" },
  { value: "active", label: "Active" },
  { value: "b2b", label: "B2B" },
  { value: "comp", label: "Comp" },
  { value: "canceling", label: "Canceling" },
  { value: "expired", label: "Expired" },
  { value: "canceled", label: "Canceled" },
  { value: "past_due", label: "Past Due" },
  { value: "none", label: "None" },
];

const USER_TYPE_OPTIONS = [
  { value: "_all", label: "All Types" },
  { value: "player", label: "Player" },
  { value: "coach", label: "Coach" },
];

const BOOLEAN_OPTIONS = [
  { value: "_any", label: "Any" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

// Labels for active filter chips
const FILTER_LABELS: Record<string, string> = {
  search: "Search",
  userType: "Type",
  subscriptionStatus: "Access Type",
  activationStage: "Activation",
  jgsLinked: "JGS Linked",
  ajgaLinked: "AJGA Linked",
  gradYear: "Grad Year",
  homeState: "Home State",
};

export function UserListFilters({ filters, onFilterChange }: UserListFiltersProps) {
  const [showLegend, setShowLegend] = useState(false);
  function update(patch: Partial<CrmUserFilters>) {
    onFilterChange({ ...filters, ...patch, skip: 0 });
  }

  function clearFilter(key: keyof CrmUserFilters) {
    const next = { ...filters, skip: 0 };
    delete next[key];
    onFilterChange(next);
  }

  // Compute active filter chips
  const activeFilters: { key: keyof CrmUserFilters; label: string; value: string }[] = [];
  if (filters.search) {
    activeFilters.push({ key: "search", label: FILTER_LABELS.search, value: filters.search });
  }
  if (filters.userType) {
    activeFilters.push({
      key: "userType",
      label: FILTER_LABELS.userType,
      value: filters.userType === "player" ? "Player" : "Coach",
    });
  }
  if (filters.subscriptionStatus) {
    const opt = SUBSCRIPTION_OPTIONS.find(o => o.value === filters.subscriptionStatus);
    activeFilters.push({
      key: "subscriptionStatus",
      label: FILTER_LABELS.subscriptionStatus,
      value: opt?.label || filters.subscriptionStatus,
    });
  }
  if (filters.activationStage) {
    const opt = ACTIVATION_OPTIONS.find(o => o.value === filters.activationStage);
    activeFilters.push({
      key: "activationStage",
      label: FILTER_LABELS.activationStage,
      value: opt?.label || filters.activationStage,
    });
  }
  if (filters.jgsLinked) {
    activeFilters.push({
      key: "jgsLinked",
      label: FILTER_LABELS.jgsLinked,
      value: filters.jgsLinked === "true" ? "Yes" : "No",
    });
  }
  if (filters.ajgaLinked) {
    activeFilters.push({
      key: "ajgaLinked",
      label: FILTER_LABELS.ajgaLinked,
      value: filters.ajgaLinked === "true" ? "Yes" : "No",
    });
  }
  if (filters.gradYear) {
    activeFilters.push({ key: "gradYear", label: FILTER_LABELS.gradYear, value: filters.gradYear });
  }
  if (filters.homeState) {
    activeFilters.push({
      key: "homeState",
      label: FILTER_LABELS.homeState,
      value: filters.homeState,
    });
  }

  return (
    <div className="space-y-3">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Search users..."
            value={filters.search || ""}
            onChange={e => update({ search: e.target.value || undefined })}
            className="h-9"
          />
        </div>
        <div className="w-32">
          <Select
            value={filters.userType || "_all"}
            onValueChange={v =>
              update({ userType: v === "_all" ? undefined : (v as "player" | "coach") })
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {USER_TYPE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-36">
          <Select
            value={filters.subscriptionStatus || "_all"}
            onValueChange={v => update({ subscriptionStatus: v === "_all" ? undefined : v })}
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUBSCRIPTION_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-32">
          <Select
            value={filters.activationStage || "_all"}
            onValueChange={v =>
              update({
                activationStage:
                  v === "_all" ? undefined : (v as "activated" | "invited" | "lapsed"),
              })
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIVATION_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <button
          type="button"
          onClick={() => setShowLegend(!showLegend)}
          className={`p-1.5 rounded-md transition-colors ${showLegend ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
          title="Status legend"
        >
          <HelpCircle className="h-4 w-4" />
        </button>
        <div className="w-28">
          <Select
            value={filters.jgsLinked || "_any"}
            onValueChange={v =>
              update({ jgsLinked: v === "_any" ? undefined : (v as "true" | "false") })
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="JGS" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">JGS: Any</SelectItem>
              {BOOLEAN_OPTIONS.filter(o => o.value !== "_any").map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  JGS: {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-28">
          <Select
            value={filters.ajgaLinked || "_any"}
            onValueChange={v =>
              update({ ajgaLinked: v === "_any" ? undefined : (v as "true" | "false") })
            }
          >
            <SelectTrigger className="h-9">
              <SelectValue placeholder="AJGA" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_any">AJGA: Any</SelectItem>
              {BOOLEAN_OPTIONS.filter(o => o.value !== "_any").map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  AJGA: {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-24">
          <Input
            type="number"
            placeholder="Grad Year"
            value={filters.gradYear || ""}
            onChange={e => update({ gradYear: e.target.value || undefined })}
            className="h-9"
          />
        </div>
        <div className="w-20">
          <Input
            placeholder="State"
            maxLength={2}
            value={filters.homeState || ""}
            onChange={e => update({ homeState: e.target.value.toUpperCase() || undefined })}
            className="h-9"
          />
        </div>
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 flex items-center gap-2">
              Access
              <span className="font-normal normal-case tracking-normal">
                Can this user use the app right now?
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
                <span className="text-xs text-muted-foreground">Has access</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
                <span className="text-xs text-muted-foreground">No access</span>
              </div>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
              Access Type
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {STATUS_LEGEND.map(s => (
                <div key={s.state} className="flex items-center gap-2">
                  <Badge variant="outline" className={`${s.className} text-xs shrink-0`}>
                    {s.label}
                  </Badge>
                  <span className="text-xs text-muted-foreground">{s.description}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
              Activation Stage
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="border-green-300 text-green-700 text-xs shrink-0"
                >
                  Activated
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Completed onboarding (has password or accepted magic link)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-blue-300 text-blue-700 text-xs shrink-0">
                  Invited
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Invitation sent, not yet acted on (token still valid)
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="border-amber-300 text-amber-700 text-xs shrink-0"
                >
                  Lapsed
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Invitation expired unused — never onboarded
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {activeFilters.map(f => (
            <Badge
              key={f.key}
              variant="secondary"
              className="pl-2 pr-1 py-1 gap-1 text-xs font-normal"
            >
              {f.label}: {f.value}
              <button
                onClick={() => clearFilter(f.key)}
                className="ml-1 rounded-full hover:bg-muted-foreground/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
